import { getColumnTitle, getColumnTooltip } from '@src/utils/fieldDefinitionUtils';
import {
  createFieldLimitedAggFunc,
  equalityAggFunc,
  getAgGridColId,
  isFieldGroupInHierarchy,
  useDefaultColumns,
  type Column,
  type ColumnDef,
} from '@talos/kyoko';
import type { GridOptions, IAggFuncParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useMemo } from 'react';
import { TradeStatusCell } from '../LPSettlementBatchesGrid/LPSettlementTradesGridCellRenderers';
import { TradeIDCell } from '../LPSettlementBatchesGrid/TradeIDCell';
import type { TradeSettlementColumn } from '../types';
import type { LPSettlementReportBlotterRow } from './blotter/lpSettlementReportRow';
import { unsettledTransactionsFieldDefinitions } from './unsettledTransactionsFieldDefinitions';

const EVENT_ID_FIELD_EQUALITY_AGG_FUNC = 'eventIdFieldEquality';

interface UseTradeSettlementColumnsParams {
  defaultColumns: TradeSettlementColumn[];
}

export const useTradeSettlementColumns = ({ defaultColumns }: UseTradeSettlementColumnsParams) => {
  const aggFuncs = useMemo<GridOptions<LPSettlementReportBlotterRow>['aggFuncs']>(
    () => ({
      [EVENT_ID_FIELD_EQUALITY_AGG_FUNC]: createFieldLimitedAggFunc('EventID', equalityAggFunc),
    }),
    []
  );
  const columnDefinitions = useMemo(() => {
    return new Map<string, Column>(
      compact([
        {
          type: 'marketAccount',
          field: 'MarketAccount',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.marketAccount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.marketAccount),
          initialWidth: 150,
          aggregate: true,
          enableRowGroup: true,
        },
        {
          type: 'date',
          field: 'SettleDeadline',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.settlementDue),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.settlementDue),
          initialSort: '-',
          sortable: true,
          aggregate: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          enableRowGroup: true,
        },
        {
          type: 'custom',
          field: 'SettleStatus',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.status),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.status),
          initialWidth: 100,
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          params: {
            cellRenderer: TradeStatusCell,
          },
        },
        {
          type: 'side',
          field: 'EventSide',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.side),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.side),
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
        },
        {
          type: 'size',
          field: 'BaseLeg.Quantity',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.quantity),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.quantity),
          initialWidth: 100,
          aggregate: true,
          params: {
            currencyField: 'BaseLeg.Asset',
            highlightNegative: true,
            aggregateIf: ({ rowNode }: IAggFuncParams) => {
              // aggregate only up to EventID group (at EventID level and below, not above)
              return isFieldGroupInHierarchy(rowNode, 'EventID');
            },
          },
        },
        {
          type: 'size',
          field: 'QuoteLeg.Amount',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.amount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.amount),
          initialWidth: 100,
          aggregate: true,
          params: {
            currencyField: 'QuoteLeg.Asset',
            highlightNegative: true,
            aggregateIf: ({ rowNode }: IAggFuncParams) => {
              // aggregate only up to EventID group (at EventID level and below, not above)
              return isFieldGroupInHierarchy(rowNode, 'EventID');
            },
          },
        },
        {
          type: 'subAccount',
          field: 'subAccounts',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.subAccount),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.subAccount),
          initialWidth: 150,
          enableRowGroup: true,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
        },
        {
          type: 'size',
          field: 'EventPrice',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.filledPrice),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.filledPrice),
          params: { currencyField: 'QuoteLeg.Asset' },
          initialWidth: 125,
        },
        {
          type: 'date',
          field: 'EventTransactTime',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.transactTime),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.transactTime),
          initialWidth: 150,
          initialAggFunc: EVENT_ID_FIELD_EQUALITY_AGG_FUNC,
          enableRowGroup: true,
        },
        {
          type: 'custom',
          field: 'EventID',
          title: getColumnTitle(unsettledTransactionsFieldDefinitions.eventId),
          description: getColumnTooltip(unsettledTransactionsFieldDefinitions.eventId),
          params: {
            cellRenderer: TradeIDCell,
          },
          initialRowGroupIndex: 0,
          enableRowGroup: true,
        },
      ] satisfies ColumnDef<LPSettlementReportBlotterRow>[]).map(c => [getAgGridColId(c), c])
    );
  }, []);

  const columns = useDefaultColumns(defaultColumns, columnDefinitions);
  return { columns, aggFuncs };
};
